/**
 * identityOtp — RPC contract for the sign-in door's one deployed callable,
 * the `identityOtp` router. Three acts ride it, each an `action` on the
 * payload: `resolveEmail` says whether an address would be mailed a code,
 * `sendOTP` mails it, `verifyOTP` proves it and mints the session. Parsed by
 * `firebase/src/auth/identityOtp.router.ts` and composed by every client
 * that signs a person in (`packages/session/src/session.api.ts`, the
 * scanner's `session.sync.ts`).
 *
 * EVERY ARM COMPOSES FROM ITS OWN CONTRACT FILE, as `invitesRouter.action.ts`
 * does: each act keeps its `Input`/`Output` beside its own justification, and
 * each input spreads onto this level through `.extend()`, keeping the
 * source's `.strict()` — an extra key still fails the whole request. All
 * three inputs are flat objects, so each is a legal option of the
 * discriminated union.
 *
 * `action` IS ADDRESSING, NOT DATA. The router takes the discriminant off
 * before the payload crosses into a handler, so a handler sees exactly the
 * contract it declares.
 *
 * THE ENVELOPE ASSERTS NO AUTHENTICATION: every act is a bootstrap door an
 * unauthenticated caller walks through, before any session exists. What
 * bounds them is App Check outside the emulator, each act's own throttle,
 * and the one shared admission (`firebase/src/auth/admission.api.ts`) that
 * decides whom a code reaches.
 *
 * Fields justified:
 *   action — resolveEmail | sendOTP | verifyOTP; selects the handler, and
 *            which payload shape applies.
 *   (every other field) — the matching act's own fields; each source
 *            contract carries its own justification.
 */
import { z } from "zod";
import {
	ResolveEmailInput,
	ResolveEmailOutput,
} from "./resolveEmail.action.ts";
import { SendOTPInput, SendOTPOutput } from "./sendOTP.action.ts";
import { VerifyOTPInput, VerifyOTPOutput } from "./verifyOTP.action.ts";

export const IdentityOtpInput = z.discriminatedUnion("action", [
	ResolveEmailInput.extend({ action: z.literal("resolveEmail") }),
	SendOTPInput.extend({ action: z.literal("sendOTP") }),
	VerifyOTPInput.extend({ action: z.literal("verifyOTP") }),
]);
export type IdentityOtpInput = z.infer<typeof IdentityOtpInput>;

// The three answers share no field to discriminate on — a resolution flag
// (`known`), a send receipt (`sent`) and a minted session (`token`) — so
// this stays a plain union.
export const IdentityOtpOutput = z.union([
	ResolveEmailOutput,
	SendOTPOutput,
	VerifyOTPOutput,
]);
export type IdentityOtpOutput = z.infer<typeof IdentityOtpOutput>;
